/*
 * Which appearance to open in, as data. No Vue, so the awkward parts — a browser that refuses to
 * remember anything, and a stored value that is not one of ours — are testable without mounting
 * anything. Deliberately shaped like `src/i18n/locale.ts`, because it is the same problem: a
 * preference somebody may or may not have expressed, remembered here and on their profile.
 *
 * **There are two appearances and "system" is not one of them.** Never having chosen is what
 * means "follow the system", exactly as a null `locale` means "never chose a language" — and it
 * has to stay distinguishable from having chosen, or the choice could never be given back. The
 * whole of the following is done by `color-scheme: light dark` in main.css, so there is nothing
 * here that detects a system preference or watches it change.
 *
 * Two ways this is *unlike* the language, both worth knowing. `bootLocale` stopped consulting
 * the browser's own languages, because a first screen in a language nobody asked for was a
 * surprise; `prefers-color-scheme` is not that — it is a setting somebody made on purpose about
 * every app on their device, and ignoring it would be the surprise. And a language needs a
 * script to apply it, where an appearance does not: the CSS is already right before the bundle
 * has loaded.
 */

import type { Subject } from '@/api/types';

/** Mirrors the API's `THEME_MODES`; the contract is what makes them agree. */
export const THEME_MODES = ['light', 'dark'] as const;

/** The appearance, never the absence of one. `Subject['theme']` is the same union plus null. */
export type ThemeMode = NonNullable<Subject['theme']>;

/** Namespaced like the session cookie and the locale key, so a shared origin cannot collide. */
export const THEME_STORAGE_KEY = 'rs_theme';

export const isThemeMode = (value: unknown): value is ThemeMode =>
  typeof value === 'string' && (THEME_MODES as readonly string[]).includes(value);

/**
 * The choice this browser remembers, or null if there is none.
 *
 * Null rather than a default, because "never chosen" and "chose light" are different things:
 * only the first may be overridden by the system, and only the second follows somebody to their
 * laptop.
 */
export function readStoredMode(): ThemeMode | null {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeMode(stored) ? stored : null;
  } catch {
    // Safari in private mode throws rather than returning null, and iOS Safari is most of this
    // app's traffic. A browser that will not remember is not a browser that cannot paint.
    return null;
  }
}

export function storeMode(mode: ThemeMode): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Same again: the choice still applies to this page, it just will not outlive it.
  }
}

/** Back to following the system, which is the absence of a stored choice rather than a value. */
export function forgetMode(): void {
  try {
    localStorage.removeItem(THEME_STORAGE_KEY);
  } catch {
    // Nothing was stored in a browser that throws here anyway.
  }
}
